import React from 'react';
import _ from 'lodash';
import { SgDateUtil, SgRenewalUtil, SgSptTripUtils } from 'saga-modules';
import SpmConstants from 'sg-spm-constants';
import SgSptConstants from 'sg-spt-constants';
import CommonConstants from 'sg-common-constants';
import SpmUtils from 'sg-spm-utils';
import moment from 'moment';
import messages from './SgCepLobSpecificUtil.messages';

function getPolicySummaryCoverLabel(productCode) {
    return messages[`CoverLevelLabel${productCode}`];
}

const getRegionContent = (region, translator) => {
    if (region === SgSptConstants.REGIONS.WORLDWIDE_INCL) {
        return translator(messages.RegionWorldIncl);
    }
    if (region === SgSptConstants.REGIONS.WORLDWIDE_EXCL) {
        return translator(messages.RegionWorldExcl);
    }
    return translator(messages.RegionEurope);
};

function getTravelSpecificPolicyInfo(id, policyLineInfo, translator) {
    const getDestinationContent = () => {
        const destinations = policyLineInfo.privateTravel.sptTripDestinations
            .map((item) => translator({
                id: `typekey.SPTDestination.${item.destination}`,
                defaultMessage: ''
            }));
        if (destinations.length > 3) {
            return translator(messages.DestinationSeeACompleteList, {
                destinationsAfterJoin: destinations.slice(0, 3).join(', '),
                hiddenCountriesCount: destinations.length - 3
            });
        }
        return destinations.join(', ');
    };

    const { productType } = policyLineInfo.privateTravel;
    if (productType === SgSptConstants.PRODUCT_TYPE.ANNUAL) {
        return (
            <>
                <div className="sg-policy-info">
                    <h3>{translator(messages.Region)}</h3>
                    <h3 id={`region_${id}`}>
                        {getRegionContent(policyLineInfo.privateTravel.region, translator)}
                    </h3>
                </div>
                <div className="sg-clear-both" />
            </>
        );
    }
    if (productType === SgSptConstants.PRODUCT_TYPE.SINGLE_TRIP) {
        return (
            <>
                <div className="sg-policy-info">
                    <h3>{translator(messages.EndDate)}</h3>
                    <h3 id={`endDate_${id}`}>
                        {SgDateUtil.getDateWithFullMonthName(policyLineInfo.privateTravel.returnDate)}
                    </h3>
                </div>
                <div className="sg-clear-both" />
                <div className="sg-policy-info">
                    <h3>{translator(messages.Destination)}</h3>
                    <h3 id={`destination_${id}`}>
                        {getDestinationContent()}
                    </h3>
                </div>
                <div className="sg-clear-both" />
            </>
        );
    }
    return '';
}

function getLobSpecificPolicyInfo(id, policy, translator) {
    const { policyLineInfo, productCode } = policy;
    switch (productCode) {
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            return getTravelSpecificPolicyInfo(id, policyLineInfo, translator);
        default:
            return '';
    }
}

function shouldHideDifferentPaymentOptionMessage(modelVM) {
    return modelVM.value.lobData.privateTravel;
}
function getAmendButtonLabelTop(policy) {
    return policy.productCode === CommonConstants.HOME_PRODUCT_CODE ? policy.policyHeader.header.split('|')[0] : policy.policyHeader.header;
}

function shouldShowChangePaymentDetailsButton(paymentDetails, productCode, isRenewable) {
    if (productCode === CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE) {
        return paymentDetails.paymentPlan !== CommonConstants.PAYMENT_PLANS.ANNUAL_UPFRONT;
    }
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return isRenewable;
    }
    return true;
}

function getProductSpecificContactPhone(productCode) {
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_MOTOR_CHEQUE_PHONE_NUMBER;
        case CommonConstants.HOME_PRODUCT_CODE:
            return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_HOME_CHEQUE_PHONE_NUMBER;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_TRAVEL_PHONE_NUMBER;
        case CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE:
            return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_SMI_CONTACT_US_NUMBER;
        default:
            return '';
    }
}

function getProductSpecificContactDetails(productCode, translator, isMobile) {
    let productSpecificWording = '';
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            productSpecificWording = messages.ForMotorInsurance;
            break;
        case CommonConstants.HOME_PRODUCT_CODE:
            productSpecificWording = messages.ForHomeInsurance;
            break;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            productSpecificWording = messages.ForTravelInsurance;
            break;
        case CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE:
            productSpecificWording = messages.ForHealthInsurance;
            break;
        default:
            productSpecificWording = '';
    }
    const phoneNumber = getProductSpecificContactPhone(productCode);
    return (
        <>
            <span className="sgContactProductName">{translator(productSpecificWording)}</span>
            {isMobile
                ? (
                    <a
                        className="sgContactPhoneNumber"
                        href={`tel:${phoneNumber.replaceAll(' ', '')}`}
                    >
                        {phoneNumber}
                    </a>
                ) : <span className="sgContactPhoneNumber">{phoneNumber}</span>}
        </>
    );
}

function getProductSpecificOpenLinesWording(productCode, translator) {
    let firstParagraph = '';
    let secondParagraph = '';

    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
        case CommonConstants.HOME_PRODUCT_CODE:
            firstParagraph = messages.OpenLinesHomeMotor1;
            secondParagraph = messages.OpenLinesHomeMotor2;
            break;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            firstParagraph = messages.OpenLinesTravel1;
            secondParagraph = messages.OpenLinesTravel2;
            break;
        case CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE:
            firstParagraph = messages.OpenLinesHealth1;
            secondParagraph = messages.OpenLinesHealth2;
            break;
        default:
            firstParagraph = '';
            secondParagraph = '';
    }

    return (
        <>
            <p>{translator(messages.OpenLines)}</p>
            <p>{translator(firstParagraph)}</p>
            <p>{translator(secondParagraph)}</p>
        </>
    );
}

function shouldShowFixedPriceInfoOnRenewal(modelVM) {
    return SgRenewalUtil.isUpgrading(modelVM.value) && !SgRenewalUtil.isTravelProduct(modelVM);
}

function showUpgradedRenewalBanner(modelVM) {
    if (SgRenewalUtil.isTravelProduct(modelVM)) {
        return false;
    }
    return true;
}

function canShowWOC3143Summary(modelVM, activeMvtTests) {
    if (SgRenewalUtil.isTravelProduct(modelVM)) {
        return false;
    }
    return activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_3143);
}

function renewalSummaryContinueButtonLabel(modelVM, activeMvtTests, translator) {
    if (activeMvtTests.includes(CommonConstants.ACTIVE_MVT_TESTS.WOC_3143) && !SgRenewalUtil.isTravelProduct(modelVM)) {
        return translator(messages.ContinueLabel_WOC3143);
    }
    if (SgRenewalUtil.isUpgrading(modelVM.value)) {
        return SgRenewalUtil.isTravelProduct(modelVM) ? translator(messages.RenewSagaPlusLabel) : translator(messages.RenewFixedPriceLabel);
    }
    return translator(messages.RenewLabel);
}

function shouldShowWebChatWordingForBlockedEndorsement(productCode) {
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return true;
    }
    return false;
}

function getCarouselArticles(isRenewal, productCode) {
    if (isRenewal) {
        switch (productCode) {
            case CommonConstants.MOTOR_PRODUCT_CODE:
                return [
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CAR_BAN,
                        name: messages.CarBanName,
                        text: {
                            header: messages.CarBanHeader,
                            description: messages.CarBanDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CAR_MOT,
                        name: messages.MOTName,
                        text: {
                            header: messages.MOTHeader,
                            description: messages.MOTDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CAR_ELECTRIC_ROAD,
                        name: messages.ElectricRoadName,
                        text: {
                            header: messages.ElectricRoadHeader,
                            description: messages.ElectricRoadDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CAR_MED_COND,
                        name: messages.CarMedCondName,
                        text: {
                            header: messages.CarMedCondHeader,
                            description: messages.CarMedCondDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CAR_EXCESS,
                        name: messages.CarExcessName,
                        text: {
                            header: messages.CarExcessHeader,
                            description: messages.CarExcessDescription
                        }
                    }
                ];
            case CommonConstants.HOME_PRODUCT_CODE:
                return [
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.REDUCE_ENERGY_BILLS,
                        name: messages.ReduceEnergyBillsName,
                        text: {
                            header: messages.ReduceEnergyBillsHeader,
                            description: messages.ReduceEnergyBillsDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.HOME_SURVEILLANCE,
                        name: messages.HomeSurveillanceName,
                        text: {
                            header: messages.HomeSurveillanceHeader,
                            description: messages.HomeSurveillanceDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.BOILER_SERVICE,
                        name: messages.BoilerServiceName,
                        text: {
                            header: messages.BoilerServiceHeader,
                            description: messages.BoilerServiceDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.HOME_FIRE,
                        name: messages.HomeFireInsuranceName,
                        text: {
                            header: messages.HomeFireInsuranceHeader,
                            description: messages.HomeFireInsuranceDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.HOME_SHED,
                        name: messages.ShedInsuranceName,
                        text: {
                            header: messages.ShedInsuranceHeader,
                            description: messages.ShedInsuranceDescription
                        }
                    }
                ];
            case CommonConstants.TRAVEL_PRODUCT_CODE:
                return [
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.HOLIDAY_CHECKLIST,
                        name: messages.HolidayChecklistName,
                        text: {
                            header: messages.HolidayChecklistHeader,
                            description: messages.HolidayChecklistDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.CANCELLED_FLIGHTS,
                        name: messages.CancelledFlightsName,
                        text: {
                            header: messages.CancelledFlightsHeader,
                            description: messages.CancelledFlightsDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.TRAVEL_GP,
                        name: messages.TravelGPName,
                        text: {
                            header: messages.TravelGPHeader,
                            description: messages.TravelGPDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.DELAYED_FLIGHT,
                        name: messages.DelayedFlightName,
                        text: {
                            header: messages.DelayedFlightHeader,
                            description: messages.DelayedFlightDescription
                        }
                    },
                    {
                        url: CommonConstants.SAGA_ARTICLES_URLS.TRAVEL_PROTECT,
                        name: messages.TravelProtectName,
                        text: {
                            header: messages.TravelProtectHeader,
                            description: messages.TravelProtectDescription
                        }
                    }
                ];
            default:
                return [];
        }
    }
    return [
        {
            url: CommonConstants.SAGA_ARTICLES_URLS.CAR_BOOKING_SERVICE,
            name: messages.BookingCarServiceName,
            text: {
                header: messages.BookingCarServiceHeader,
                description: messages.BookingCarServiceDescription
            }
        },
        {
            url: CommonConstants.SAGA_ARTICLES_URLS.CARS_FOR_OLDER_DRIVERS,
            name: messages.CarsForOlderDriversName,
            text: {
                header: messages.CarsForOlderDriversHeader,
                description: messages.CarsForOlderDriversDescription
            }
        },
        {
            url: CommonConstants.SAGA_ARTICLES_URLS.CAR_FOR_WOMEN,
            name: messages.CarInsuranceForWomenName,
            text: {
                header: messages.CarInsuranceForWomenHeader,
                description: messages.CarInsuranceForWomenDescription
            }
        },
        {
            url: CommonConstants.SAGA_ARTICLES_URLS.HOW_TO_MAKE_CLAIM,
            name: messages.HowToMakeClaimName,
            text: {
                header: messages.HowToMakeClaimHeader,
                description: messages.HowToMakeClaimDescription
            }
        },
        {
            url: CommonConstants.SAGA_ARTICLES_URLS.CAR_DRIVING_OTHER_CARS,
            name: messages.DrivingOtherCarsName,
            text: {
                header: messages.DrivingOtherCarsHeader,
                description: messages.DrivingOtherCarsDescription
            }
        }
    ];
}

function getCoverTypeMessage(productCode) {
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return messages.AnnualTrip;
    }
    return '';
}

function getCoverLevel(productCode, modelVM) {
    if (productCode === CommonConstants.HOME_PRODUCT_CODE) {
        return CommonConstants.HOME_COVER_TYPES[modelVM.lobData.privateHome.coverables.coverLevel.value.code.toUpperCase()];
    }
    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        const vehicle = modelVM.lobData.privateMotor.coverables.spmVehicle.value;
        let coverLevelName;
        if (vehicle.coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_STANDARD) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_STANDARD;
        }
        if (vehicle.coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_SELECT) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_COMPREHENSIVE;
        }
        if (vehicle.coverLevel === SpmConstants.COVER_LEVEL_CODES.SAGA_PLUS) {
            coverLevelName = SpmConstants.SG_COVER_LEVELS.MOTOR_FIXED_PRICE;
        }
        return coverLevelName;
    }
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        switch (SgSptTripUtils.getSelectedTrip(modelVM).coverLevel) {
            case SgSptConstants.COVER_LEVELS.STANDARD:
                return CommonConstants.COVER_LEVEL_NAMES.STANDARD;
            case SgSptConstants.COVER_LEVELS.SELECT:
                return CommonConstants.COVER_LEVEL_NAMES.SELECT;
            case SgSptConstants.COVER_LEVELS.PLUS:
                return CommonConstants.COVER_LEVEL_NAMES.PLUS;
            case SgSptConstants.COVER_LEVELS.ESSENTIAL:
                return CommonConstants.COVER_LEVEL_NAMES.ESSENTIAL;
            default:
                break;
        }
    }
    return '';
}

function getProductSummaryInfoMessage(productCode) {
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            return messages.VehicleInfo;
        case CommonConstants.HOME_PRODUCT_CODE:
            return messages.AddressInfo;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            return messages.DestinationInfo;
        default:
            return '';
    }
}

function getProductSummaryInfoParameters(productCode, modelVM, translator) {
    if (productCode === CommonConstants.HOME_PRODUCT_CODE) {
        const address = modelVM.lobData.privateHome.coverables.propertyDetails.location.policyLocation.value;
        const countyDetail = address.county ? `${address.county},` : '';
        return {
            addressLine1: address.addressLine1,
            addressLine2: address.addressLine2,
            addressLine3: address.addressLine3,
            city: address.city,
            county: countyDetail,
            postalCode: address.postalCode
        };
    }
    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        const vehicle = modelVM.lobData.privateMotor.coverables.spmVehicle.value;
        return {
            make: vehicle.make,
            model: vehicle.model,
            registrationNumber: vehicle.registrationNumber
        };
    }
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return {
            destination: getRegionContent(SgSptTripUtils.getSelectedTrip(modelVM).region, translator)
        };
    }
    return {};
}

function shouldShowConfirmationPaymentMethod(productCode) {
    if (productCode === CommonConstants.TRAVEL_PRODUCT_CODE) {
        return false;
    }
    return true;
}

function getProductName(productCode) {
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            return CommonConstants.MOTOR_PRODUCT_DISPLAY;
        case CommonConstants.HOME_PRODUCT_CODE:
            return CommonConstants.HOME_PRODUCT_DISPLAY;
        case CommonConstants.TRAVEL_PRODUCT_CODE:
            return CommonConstants.TRAVEL_PRODUCT_DISPLAY;
        default:
            return '';
    }
}

const getModificationString = ((modifications) => {
    let modificationString = '';
    _.each(modifications, (mod, index) => {
        if (index > 0) {
            modificationString += ', ';
        }
        modificationString += mod;
    });
    return modificationString;
});

function getChangesAdded(productCode, changes, translator) {
    const changesAdded = [];

    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        if (changes.addedDriver) {
            changesAdded.push({
                id: 'driver_added',
                message: messages.DriverAdded,
                params: {
                    driver: `${changes.addedDriver.person.firstName} ${changes.addedDriver.person.lastName},
                        DOB: ${SgDateUtil.toStringShort(changes.addedDriver.person.dateOfBirth)}`
                }
            });
        }
        if (changes.vehicle) {
            changesAdded.push({
                id: 'vehicle_change',
                message: messages.VehicleChange,
                params: {
                    vehicle: `${changes.vehicle.registration}, ${changes.vehicle.make}, ${changes.vehicle.model}`
                }
            });
        }
        if (changes.registrationNumber) {
            changesAdded.push({
                id: 'reg_change',
                message: messages.RegChange,
                params: {
                    registration: changes.registrationNumber
                }
            });
        }
        if (changes.addedModification) {
            changesAdded.push({
                id: 'modification_added',
                message: messages.ModificationAdded,
                params: {
                    modification: getModificationString(changes.addedModification)
                }
            });
        }
        if (changes.mileage) {
            changesAdded.push({
                id: 'mileage_change',
                message: messages.MileageChange,
                params: {
                    mileage: translator({
                        id: `typekey.Mileage_Ext.${changes.mileage.code}`,
                        defaultMessage: ''
                    })
                }
            });
        }
        if (changes.vehicleUse) {
            changesAdded.push({
                id: 'vehicle_use_change',
                message: messages.VehicleUseChange,
                params: {
                    use: translator({
                        id: `typekey.VehicleUse_Ext.${changes.vehicleUse.code}`,
                        defaultMessage: ''
                    })
                }
            });
        }
        if (changes.vehicleParkingType) {
            changesAdded.push({
                id: 'vehicle_parking_change',
                message: messages.VehicleParkingChange,
                params: {
                    parking: translator({
                        id: `typekey.VehicleParkingType_Ext.${changes.vehicleParkingType.code}`,
                        defaultMessage: ''
                    })
                }
            });
        }
        if (changes.coverageAdded) {
            _.each(changes.coverageAdded, (coverage) => {
                changesAdded.push({
                    id: coverage.publicID,
                    message: messages.CoverageAdded,
                    params: {
                        coverage: coverage.name
                    }
                });
            });
        }
    }
    return changesAdded;
}

function getChangesRemoved(productCode, changes) {
    const changesRemoved = [];

    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        if (changes.removedDriver) {
            changesRemoved.push({
                id: 'driver_removed',
                message: messages.DriverRemoved,
                params: {
                    driver: `${changes.removedDriver.person.firstName} ${changes.removedDriver.person.lastName},
                        DOB: ${SgDateUtil.toStringShort(changes.removedDriver.person.dateOfBirth)}`
                }
            });
        }
        if (changes.removedModification) {
            changesRemoved.push({
                id: 'modification_removed',
                message: messages.ModificationRemoved,
                params: {
                    modification: getModificationString(changes.removedModification)
                }
            });
        }
        if (changes.coverageRemoved) {
            _.each(changes.coverageRemoved, (coverage) => {
                changesRemoved.push({
                    id: coverage.publicID,
                    message: messages.CoverageRemoved,
                    params: {
                        coverage: coverage.name
                    }
                });
            });
        }
    }
    return changesRemoved;
}

function getProductsToHideInSagaOtherProducts(productCode) {
    return {
        hideMotor: productCode === CommonConstants.MOTOR_PRODUCT_CODE,
        hideHome: productCode === CommonConstants.HOME_PRODUCT_CODE,
        hideSmi: productCode === CommonConstants.PMI_PRODUCT_CODE,
        hideTravel: productCode === CommonConstants.TRAVEL_PRODUCT_CODE
    };
}

function shouldBlockThirdPartyPolicy(productCode, modelVM) {
    if (productCode === CommonConstants.MOTOR_PRODUCT_CODE) {
        if (SpmUtils.isThirdPartyOnly(modelVM) || SpmUtils.isThirdPartyFireAndTheft(modelVM)) {
            return true;
        }
    }
    return false;
}

function getProductSpecificMTAContactPhone(productCode) {
    switch (productCode) {
        case CommonConstants.MOTOR_PRODUCT_CODE:
            return CommonConstants.SAGA_PHONE_NUMBERS.SAGA_CEP_FAQ_MOTOR_PHONE_NUMBER1;
        default:
            return '';
    }
}

function getProductSpecificWebChatOpenTime(productCode) {
    const isSptOrSmi = productCode === CommonConstants.TRAVEL_PRODUCT_CODE || productCode === CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE;
    return {
        startTime: moment('8:30:00', 'hh:mm:ss'),
        endTime: isSptOrSmi ? moment('19:00:00', 'hh:mm:ss') : moment('20:00:00', 'hh:mm:ss'),
        saturdayEndTime: productCode === CommonConstants.PRIVATE_MEDICAL_INSURANCE_CODE
            ? moment('13:00:00', 'hh:mm:ss') : moment('17:00:00', 'hh:mm:ss')
    };
}

function show003EndorsementDeclarations(modelVM) {
    if (SgRenewalUtil.isHomeProduct(modelVM)) {
        const has003Endorsement = _.some(modelVM.value.lobData.privateHome.offerings[0].coverages.endorsements, (el) => parseInt(el.code, 10) === 3);
        return has003Endorsement;
    }
    return false;
}

function showAbilityToPayInfo(productCode) {
    return productCode === CommonConstants.MOTOR_PRODUCT_CODE || productCode === CommonConstants.HOME_PRODUCT_CODE;
}

export default {
    getPolicySummaryCoverLabel,
    getLobSpecificPolicyInfo,
    getProductSpecificContactPhone,
    canShowWOC3143Summary,
    shouldHideDifferentPaymentOptionMessage,
    getAmendButtonLabelTop,
    shouldShowChangePaymentDetailsButton,
    getProductSpecificContactDetails,
    getProductSpecificOpenLinesWording,
    shouldShowFixedPriceInfoOnRenewal,
    showUpgradedRenewalBanner,
    renewalSummaryContinueButtonLabel,
    shouldShowWebChatWordingForBlockedEndorsement,
    getCarouselArticles,
    getCoverTypeMessage,
    getCoverLevel,
    getProductSummaryInfoMessage,
    getProductSummaryInfoParameters,
    getProductName,
    shouldShowConfirmationPaymentMethod,
    getChangesAdded,
    getChangesRemoved,
    shouldBlockThirdPartyPolicy,
    getProductSpecificMTAContactPhone,
    getProductSpecificWebChatOpenTime,
    getProductsToHideInSagaOtherProducts,
    show003EndorsementDeclarations,
    showAbilityToPayInfo
};
